import { useState } from "react";
import { useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, KeyRound, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { getSupabaseClient } from "../../providers/supabase/supabase";

type ApiKey = {
  workspace_id: number;
  service: string;
  label: string;
  paid: boolean;
  enabled: boolean;
  last4: string | null;
  saved_at: string | null;
  tested_at: string | null;
  test_ok: boolean | null;
  test_msg: string | null;
};

// Dónde se consigue cada llave
const WHERE: Record<string, string> = {
  gemini: "https://aistudio.google.com/apikey",
  groq: "https://console.groq.com/keys",
  openrouter: "https://openrouter.ai/settings/keys",
  cerebras: "https://cloud.cerebras.ai/platform",
  cloudflare: "https://dash.cloudflare.com/profile/api-tokens",
  cloudflare_account: "https://dash.cloudflare.com/?to=/:account/workers/ai",
  tavily: "https://app.tavily.com/home",
  serpapi: "https://serpapi.com/manage-api-key",
  denue: "https://www.inegi.org.mx/app/api/denue/v1/tokenVerify.aspx",
  deepseek: "https://platform.deepseek.com/api_keys",
  claude: "https://console.anthropic.com/settings/keys",
  outscraper: "https://app.outscraper.com/profile",
};

const fecha = (s: string) =>
  new Date(s).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

/**
 * Llaves y conexiones. Cada usuario ve y edita solo las llaves de SU espacio de
 * trabajo (RLS en api_keys; guardar pasa por api_key_set, que solo acepta al dueño
 * del espacio o a un administrador). La llave se guarda cifrada en el Vault del
 * servidor; aquí solo se ve "····1234". Probar conexión la usa desde el servidor
 * (edge function api-key-test) con una consulta gratuita.
 */
export const KeysSection = () => {
  const sb = getSupabaseClient();
  const { data: workspace } = useQuery({
    queryKey: ["api_keys_workspace"],
    queryFn: async () => {
      const { data, error } = await sb.rpc("default_workspace_id");
      if (error) throw error;
      return data as number | null;
    },
  });
  const { data: isAdmin } = useQuery({
    queryKey: ["is_admin"],
    queryFn: async () => {
      const { data, error } = await sb.rpc("is_admin");
      if (error) throw error;
      return !!data;
    },
  });
  const { data: keys, error } = useQuery({
    queryKey: ["api_keys", workspace],
    enabled: workspace != null,
    queryFn: async () => {
      const { data, error } = await sb
        .from("api_keys")
        .select("*")
        .eq("workspace_id", workspace!)
        .order("sort");
      if (error) throw error;
      return data as ApiKey[];
    },
  });
  // Sin filas: no es el dueño de su espacio ni administrador (RLS no devuelve nada)
  if (error || workspace == null || !keys?.length) return null;
  return (
    <Card className="gap-3 p-4">
      <h2 className="flex items-center gap-2 text-base font-extrabold">
        <KeyRound className="size-5 text-primary" />
        Llaves y conexiones
      </h2>
      <p className="text-sm text-muted-foreground">
        Estas llaves son de tu espacio de trabajo: nadie más las ve ni las usa.
        Pega cada llave una vez: se guarda cifrada en el servidor y nunca se
        vuelve a mostrar completa (ni va a GitHub ni a los respaldos). Los
        servicios de pago quedan apagados hasta que los actives.
      </p>
      <p className="rounded-lg border bg-muted/40 p-3 text-sm">
        Si activas facturación en tu cuenta del proveedor, los cobros son tuyos.
      </p>
      {isAdmin && <RouterSwitch />}
      <div className="grid gap-3 lg:grid-cols-2">
        {keys.map((k) => (
          <KeyRow key={k.service} k={k} workspace={workspace} />
        ))}
      </div>
    </Card>
  );
};

/** Interruptor del enrutador de IA (apagado por defecto). Solo el administrador lo ve y lo cambia. */
const RouterSwitch = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const sb = getSupabaseClient();
  const { data: on } = useQuery({
    queryKey: ["crm_settings", "ai_router_enabled"],
    queryFn: async () => {
      const { data, error } = await sb
        .from("crm_settings")
        .select("ai_router_enabled")
        .eq("id", 1)
        .single();
      if (error) throw error;
      return data.ai_router_enabled as boolean;
    },
  });
  const toggle = async (value: boolean) => {
    const { error } = await sb
      .from("crm_settings")
      .update({ ai_router_enabled: value })
      .eq("id", 1);
    if (error) notify(error.message, { type: "error" });
    queryClient.invalidateQueries({
      queryKey: ["crm_settings", "ai_router_enabled"],
    });
  };
  return (
    <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
      <Switch
        checked={!!on}
        onCheckedChange={toggle}
        aria-label="Enrutador de IA"
      />
      <span>
        <b>Enrutador de IA (Groq, Cerebras, Cloudflare, OpenRouter)</b>
        <br />
        <span className="text-muted-foreground">
          {on
            ? "Encendido: la IA usa solo estas llaves, en ese orden de respaldo."
            : "Apagado: la IA funciona como siempre. Enciéndelo cuando tengas al menos una llave guardada."}
        </span>
      </span>
    </label>
  );
};

const KeyRow = ({ k, workspace }: { k: ApiKey; workspace: number }) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const sb = getSupabaseClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["api_keys", workspace] });

  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    try {
      await fn();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setBusy(null);
    refresh();
  };
  const save = () =>
    run("save", async () => {
      const { error } = await sb.rpc("api_key_set", {
        p_workspace: workspace,
        p_service: k.service,
        p_value: value,
      });
      if (error) throw new Error(error.message);
      setValue("");
      notify(`Llave de ${k.label} guardada`, { type: "success" });
    });
  const remove = () => {
    if (!window.confirm(`¿Borrar la llave de ${k.label}?`)) return;
    run("delete", async () => {
      const { error } = await sb.rpc("api_key_delete", {
        p_workspace: workspace,
        p_service: k.service,
      });
      if (error) throw new Error(error.message);
      notify(`Llave de ${k.label} borrada`, { type: "info" });
    });
  };
  const test = () =>
    run("test", async () => {
      const { data, error } = await sb.functions.invoke("api-key-test", {
        body: { service: k.service },
      });
      if (error) throw new Error("No se pudo probar: intenta de nuevo.");
      notify(data.msg, { type: data.ok ? "success" : "warning" });
    });
  const enable = (on: boolean) =>
    run("enable", async () => {
      const { error } = await sb.rpc("api_key_enable", {
        p_workspace: workspace,
        p_service: k.service,
        p_enabled: on,
      });
      if (error) throw new Error(error.message);
    });

  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <b>{k.label}</b>
        {k.paid && <Badge variant="outline">de pago</Badge>}
        <a
          href={WHERE[k.service]}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          Conseguir llave <ExternalLink className="size-3" />
        </a>
        {k.paid && (
          <label className="ml-auto flex items-center gap-2 text-sm">
            <Switch
              checked={k.enabled}
              disabled={!!busy}
              onCheckedChange={enable}
              aria-label={`Activar ${k.label}`}
            />
            {k.enabled ? "Activada" : "Apagada"}
          </label>
        )}
      </div>
      <p className="text-sm">
        {k.last4 ? (
          <>
            Guardada <span className="font-mono">····{k.last4}</span> ·{" "}
            {fecha(k.saved_at!)}
          </>
        ) : (
          <span className="text-muted-foreground">Sin llave</span>
        )}
        {k.paid && !k.enabled && (
          <span className="text-muted-foreground">
            {" "}
            · el CRM no la usará hasta que la actives
          </span>
        )}
      </p>
      {k.test_msg && (
        <p
          className={
            "flex items-start gap-1.5 text-sm " +
            (k.test_ok ? "text-[var(--good)]" : "text-[var(--critical)]")
          }
        >
          {k.test_ok ? (
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          ) : (
            <XCircle className="mt-0.5 size-4 shrink-0" />
          )}
          <span>
            {k.test_msg}
            {k.tested_at && (
              <span className="text-muted-foreground">
                {" "}
                ({fecha(k.tested_at)})
              </span>
            )}
          </span>
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Input
          type="password"
          autoComplete="off"
          placeholder={k.last4 ? "Pega la llave nueva" : "Pega la llave"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-9 min-w-40 flex-1"
          aria-label={`Llave de ${k.label}`}
        />
        <Button
          size="sm"
          disabled={!value.trim() || !!busy}
          onClick={save}
          className="h-9"
        >
          {busy === "save" ? "Guardando…" : k.last4 ? "Reemplazar" : "Guardar"}
        </Button>
      </div>
      {k.last4 && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={!!busy} onClick={test}>
            {busy === "test" ? "Probando…" : "Probar conexión"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!!busy}
            onClick={remove}
            className="text-[var(--critical)]"
          >
            Borrar
          </Button>
        </div>
      )}
    </div>
  );
};
